import React, { Dispatch, SetStateAction } from 'react';
import { Flex, Typography, Tag, message } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import moment from 'moment';
import { FileDownloadButton } from './FileDownloadButton';
import { FileInfo, JobParamsJson } from '../types';
import { extractFileNameFromS3Key, fetchFile1UrlOnDemand, fetchFile2UrlOnDemand } from '../utils/fileUtils';

interface JobDetailsColumnProps {
	record: any;
	fileInfoByJobId: { [jobId: string]: { file1: FileInfo; file2: FileInfo | null } };
	setFileInfoByJobId: Dispatch<SetStateAction<{ [jobId: string]: { file1: FileInfo; file2: FileInfo | null } }>>;
	allSheetsMetaData: any;
	onGetBatchJobResultSheetTitle: (
		spreadSheetId: string,
		batchJobId: string,
		setIsLoading: Dispatch<SetStateAction<boolean>>,
		setCurrentSheetMetaData: Dispatch<SetStateAction<any>>
	) => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
	setCurrentSheetMetaData: Dispatch<SetStateAction<any>>;
}

/**
 * Component for rendering the Job Details column in the batch jobs table
 */
export const JobDetailsColumn: React.FC<JobDetailsColumnProps> = ({
	record,
	fileInfoByJobId,
	setFileInfoByJobId,
	allSheetsMetaData,
	onGetBatchJobResultSheetTitle,
	setIsLoading,
	setCurrentSheetMetaData,
}) => {
	// Parse job_params_json string
	const parseJobParams = (jobParamsJson: string | null | undefined): JobParamsJson | null => {
		if (!jobParamsJson) return null;
		try {
			return JSON.parse(jobParamsJson) as JobParamsJson;
		} catch (error) {
			console.error("Error parsing job_params_json:", error);
			return null;
		}
	};

	// Fetch presigned URL for file1 on-demand
	const fetchFile1Url = async (jobId: string) => {
		// Check if already loading or has URL
		const currentFileInfo = fileInfoByJobId[jobId];
		if (!currentFileInfo?.file1 || currentFileInfo.file1.url || currentFileInfo.file1.loading) {
			return currentFileInfo?.file1?.url || null;
		}

		const file1InfoToFetch = currentFileInfo.file1;

		// Set loading state
		setFileInfoByJobId(prev => {
			const fileInfo = prev[jobId];
			if (!fileInfo?.file1 || fileInfo.file1.url || fileInfo.file1.loading) {
				return prev;
			}
			return {
				...prev,
				[jobId]: {
					...fileInfo,
					file1: { ...fileInfo.file1, loading: true, error: null },
				}
			};
		});

		try {
			const url = await fetchFile1UrlOnDemand(file1InfoToFetch);
			setFileInfoByJobId(prev => ({
				...prev,
				[jobId]: {
					...prev[jobId],
					file1: { ...prev[jobId].file1, url, loading: false, error: null }
				}
			}));
			return url;
		} catch (error: any) {
			const errorMessage = error.response?.status === 404
				? "File not found - This file may have been removed from S3"
				: error.response?.data?.message || error.message || "Unable to fetch file - Please try again later";
			setFileInfoByJobId(prev => ({
				...prev,
				[jobId]: {
					...prev[jobId],
					file1: { ...prev[jobId].file1, loading: false, error: errorMessage }
				}
			}));
			throw error;
		}
	};

	// Fetch presigned URL for file2 on-demand
	const fetchFile2Url = async (jobId: string) => {
		// Check if already loading or has URL
		const currentFileInfo = fileInfoByJobId[jobId];
		if (!currentFileInfo?.file2 || currentFileInfo.file2.url || currentFileInfo.file2.loading) {
			return currentFileInfo?.file2?.url || null;
		}

		const file2InfoToFetch = currentFileInfo.file2;

		// Set loading state
		setFileInfoByJobId(prev => {
			const fileInfo = prev[jobId];
			if (!fileInfo?.file2 || fileInfo.file2.url || fileInfo.file2.loading) {
				return prev;
			}
			return {
				...prev,
				[jobId]: {
					...fileInfo,
					file2: { ...fileInfo.file2, loading: true, error: null }
				}
			};
		});

		try {
			const url = await fetchFile2UrlOnDemand(file2InfoToFetch);
			setFileInfoByJobId(prev => ({
				...prev,
				[jobId]: {
					...prev[jobId],
					file2: { ...prev[jobId].file2!, url, loading: false, error: null }
				}
			}));
			return url;
		} catch (error: any) {
			// Check if this is a program file error (has programId)
			const isProgramFile = file2InfoToFetch.programId;
			const errorMessage = error.response?.status === 404
				? (isProgramFile 
					? "Program not found - Unable to retrieve structure file"
					: "File not found - This file may have been removed from S3")
				: error.response?.data?.message || error.message || (isProgramFile
					? "Unable to fetch program file - Please try again later"
					: "Unable to fetch file - Please try again later");
			setFileInfoByJobId(prev => ({
				...prev,
				[jobId]: {
					...prev[jobId],
					file2: { ...prev[jobId].file2!, loading: false, error: errorMessage }
				}
			}));
			throw error;
		}
	};

	// Initialize file info with just names (synchronously) from API response or job params
	const initializeFileInfo = (jobId: string, jobParams: JobParamsJson, record: any) => {
		// Use file names from API response if available, otherwise extract from s3Key
		// Note: makeKeyInLowerCaseHelper converts keys to lowercase, so use lowercase keys
		const file1Name = record.file1name || (jobParams.file1 ? extractFileNameFromS3Key(jobParams.file1) : '');
		const file1Info: FileInfo = {
			name: file1Name,
			url: null,
			loading: false,
			error: null,
			s3Key: jobParams.file1,
		};
		
		let file2Info: FileInfo | null = null;
		if (jobParams.file2 === "none" && jobParams.programId) {
			// For program files, use programFileName from API response if available
			// Note: makeKeyInLowerCaseHelper converts keys to lowercase, so use lowercase keys
			// Use programfilename (lowercase) from API response, or fallback to 'Program File' if not available
			const programFileName = record.programfilename || 'Program File';
			file2Info = {
				name: programFileName,
				url: null,
				loading: false,
				error: null,
				programId: jobParams.programId,
				s3Key: record.programfiles3key || undefined,
			};
		} else if (jobParams.file2 !== "none") {
			// Use file2name (lowercase) from API response if available, otherwise extract from s3Key
			const file2Name = record.file2name || (jobParams.file2 ? extractFileNameFromS3Key(jobParams.file2) : '');
			file2Info = {
				name: file2Name,
				url: null,
				loading: false,
				error: null,
				s3Key: jobParams.file2,
			};
		}

		setFileInfoByJobId(prev => ({
			...prev,
			[jobId]: {
				file1: file1Info,
				file2: file2Info,
			}
		}));
	};

	const jobCreatedAt = moment(record.job_created_at).local();
	const jobCreatedAtFormatted = jobCreatedAt.format("MM/DD/YY HH:mm");
	const toEmail = record.useremail;
	const jobStatus = record.job_status?.status || record.job_status;
	const jobMessage = record.job_status?.messsage;
	let spreadsheetURL: string | null = null;
	let spreadSheetId: string | null = null;

	// Safely check for spreadsheetURL
	if (record.job_status?.spreadsheetURL && typeof record.job_status.spreadsheetURL === 'string' && record.job_status.spreadsheetURL.length > 0) {
		spreadsheetURL = record.job_status.spreadsheetURL.includes("http")
			? record.job_status.spreadsheetURL
			: null;
		if (spreadsheetURL) {
			const urlParts = spreadsheetURL.split("/");
			spreadSheetId = urlParts[urlParts.length - 2];
		}
	}
	let spreadSheetTitle =
		spreadSheetId &&
		allSheetsMetaData[spreadSheetId] &&
		allSheetsMetaData[spreadSheetId].title;
	spreadSheetTitle = spreadSheetTitle || "Result Sheet";

	const jobParams = parseJobParams(record.job_params_json);
	const jobId = record.id;
	const fileInfo = fileInfoByJobId[jobId];

	// Initialize file info synchronously if not already done (use names from API response)
	if (!fileInfo && jobParams && jobParams.file1) {
		initializeFileInfo(jobId, jobParams, record);
	}

	const file1Info = fileInfo?.file1;
	const file2Info = fileInfo?.file2;

	return (
		<Flex vertical={true} align="start" gap={4}>
			<Flex justify="left" align="start" gap={8}>
				<Typography.Text strong>Job Created:</Typography.Text>
				<Typography.Text>{jobCreatedAtFormatted}</Typography.Text>
			</Flex>
			<Flex justify="left" align="start" gap={8}>
				<Typography.Text strong>To Email:</Typography.Text>
				<Typography.Text>{toEmail}</Typography.Text>
			</Flex>
			<Flex justify="left" align="start" gap={8}>
				<Typography.Text strong>Status:</Typography.Text>
				<Tag
					color={
						jobStatus === "RUNNING"
							? "#108ee9"
							: jobStatus === "FAILED"
							? "#f50"
							: jobStatus === "FINISHED"
							? "#87d068"
							: "blue"
					}
					data-id={record.id}
					data-created-at={jobCreatedAt.format("YYYY-MM-DD HH:mm:ss")}
					className={"batch-job-status"}
				>
					{jobStatus}
				</Tag>
			</Flex>
			{jobMessage && jobStatus === "FAILED" && (
				<Typography.Text type="danger">{jobMessage}</Typography.Text>
			)}
			<Typography.Text strong>Mode: {record.autopoddermode}</Typography.Text>
			
			{/* Input Files Section */}
			{!jobParams ? (
				<Flex vertical={true} align="start" gap={4} style={{ marginTop: 8 }}>
					<Typography.Text type="secondary" style={{ fontSize: '12px' }}>
						Invalid job parameters - Unable to parse file information
					</Typography.Text>
				</Flex>
			) : (
				<Flex vertical={true} align="start" gap={4} style={{ marginTop: 8 }}>
					{/* File 1 */}
					{file1Info && (
						<Flex justify="left" align="start" gap={8} style={{ width: '100%' }}>
							<FileDownloadButton
								fileInfo={file1Info}
								onFetchUrl={async () => {
									const url = await fetchFile1Url(jobId);
									if (url) {
										window.open(url, '_blank');
									}
								}}
								defaultLabel="File 1"
							/>
						</Flex>
					)}

					{/* File 2 */}
					{jobParams.file2 === "none" ? (
						file2Info ? (
							<Flex justify="left" align="start" gap={8} style={{ width: '100%' }}>
							<FileDownloadButton
								fileInfo={file2Info}
								onFetchUrl={async () => {
									const url = await fetchFile2Url(jobId);
									if (url) {
										window.open(url, '_blank');
									}
								}}
								defaultLabel="File 2"
							/>
							</Flex>
						) : null
					) : (
						file2Info ? (
							<Flex justify="left" align="start" gap={8} style={{ width: '100%' }}>
							<FileDownloadButton
								fileInfo={file2Info}
								onFetchUrl={async () => {
									const url = await fetchFile2Url(jobId);
									if (url) {
										window.open(url, '_blank');
									}
								}}
								defaultLabel="File 2"
							/>
							</Flex>
						) : null
					)}
				</Flex>
			)}
			
			{spreadsheetURL && (
				<Flex justify="left" align="start" gap={4}>
					<a
						href={spreadsheetURL}
						target="_blank"
						rel="noreferrer"
						className="regular-link"
						title={spreadSheetTitle}
					>
						{spreadSheetTitle.length > 40
							? `${spreadSheetTitle.substring(0, 40)}...`
							: spreadSheetTitle}
					</a>
					<a
						onClick={() => {
							if (spreadSheetId) {
								onGetBatchJobResultSheetTitle(
									spreadSheetId,
									record.id,
									setIsLoading,
									setCurrentSheetMetaData
								);
							}
						}}
					>
						<SyncOutlined style={{ color: "#1677ff", cursor: "pointer" }} />
					</a>
				</Flex>
			)}
		</Flex>
	);
};
